import {icons} from '../../libs/shared/ui/src/components/icon/icon';

/**
 * For use with sanitizeHtml from domPurify. Replaces all IMG tags with placeholder elements that include an
 * icon and alt text, if provided.
 *
 * @param currentNode The current element provided to the uponSanitizeElement method.
 */
export function replaceImageNodesWithPlaceholder(currentNode: Element) {
  // Only applies to images with parent nodes (the parent node is necessary to `replaceChild`).
  const document = currentNode.ownerDocument;
  if (
    !document ||
    !currentNode.tagName ||
    currentNode.tagName.toLowerCase() !== 'img' ||
    !currentNode.parentNode
  ) {
    return;
  }

  const placeholderText = createTextForPlaceholder(currentNode);
  const placeholder = createImagePlaceholder(currentNode, placeholderText !== null);

  placeholder.appendChild(createIconForPlaceholder(document, placeholderText !== null));

  if (placeholderText) {
    placeholder.appendChild(placeholderText);
  }

  currentNode.parentNode.replaceChild(placeholder, currentNode);
}

/* Get text for the image placeholder. */
function createTextForPlaceholder(imageNode: Element) {
  const document = imageNode.ownerDocument;
  if (!document) {
    return null;
  }

  const altText = imageNode.getAttribute('alt');
  if (!altText || altText.length === 0) {
    return null;
  }

  const textNode = document.createElement('span');
  textNode.setAttribute('style', 'line-height: 16px; align-self: center;');
  textNode.textContent = altText;

  return textNode;
}

/* Get the icon for the image placeholder. */
function createIconForPlaceholder(document: Document, hasAssociatedText: boolean) {
  const baseStyle = 'color: #9B9C9E; width: 16px; min-width: 16px; height: 16px;';
  const style = hasAssociatedText
    ? `${baseStyle} padding-right: 7px; align-self: center;`
    : `${baseStyle} vertical-align: text-bottom`;

  const rawIconSVG = icons.imageSmall;
  const imageIconSVG = document.createElement('svg');
  // eslint-disable-next-line no-restricted-syntax
  imageIconSVG.innerHTML = rawIconSVG.content;

  imageIconSVG.setAttribute('width', '16');
  imageIconSVG.setAttribute('height', '16');
  imageIconSVG.setAttribute('viewbox', '0 0 16 16');
  imageIconSVG.setAttribute('style', style);

  return imageIconSVG;
}

/* Create the image placeholder. */
function createImagePlaceholder(imageNode: Element, hasPlaceholderText: boolean) {
  const document = imageNode.ownerDocument;
  if (!document) {
    throw new Error('Cannot get document from node');
  }

  const isEmbeddedNode = isEmbeddedElement(imageNode);

  const placeholder = document.createElement('span');

  const imageWidth = imageNode.getAttribute('width');
  const imageHeight = imageNode.getAttribute('height');
  let styleWidth = '';
  let styleHeight = '';

  if (imageWidth) {
    placeholder.setAttribute('width', imageWidth);
    styleWidth = `width: ${imageWidth}px;`;
  }

  if (imageHeight) {
    placeholder.setAttribute('height', imageHeight);
    styleHeight = `height: ${imageHeight}px;`;
  }

  const baseStyle = 'line-height: 16px; font-size: 12px;';
  const imageWithTextStyle = `display: inline-flex; align-items: baseline; flex-wrap: wrap;`;
  const borderStyle = 'border: 1px solid #E8EAED; border-radius: 2px 2px 2px 2px;';
  const extraPadding = 'padding: 10px;';

  let style = baseStyle;

  if (imageHeight && imageWidth) {
    style = `${style} ${styleWidth} ${styleHeight}`;
  }

  if (hasPlaceholderText) {
    style = `${style} ${imageWithTextStyle}`;
  }

  if (!isEmbeddedNode) {
    style = `${style} ${extraPadding} ${borderStyle}`;
  }

  placeholder.setAttribute('style', style);

  return placeholder;
}

/* Determine if an HTML element is "embedded" in text (i.e. looks to the user like it is part of a paragraph). */
function isEmbeddedElement(ele: Element) {
  const currentEle =
    ele.parentElement !== null && ele.parentElement.nodeName === 'A' ? ele.parentElement : ele;

  if (!currentEle.previousSibling && !currentEle.nextSibling) {
    return false;
  }

  const prevSiblingIsText =
    currentEle.previousSibling !== null &&
    isNodeDisplayInlineByDefault(currentEle.previousSibling.nodeName) &&
    currentEle.previousSibling.nodeValue &&
    currentEle.previousSibling.nodeValue.trim().length > 0;

  const nextSiblingIsText =
    currentEle.nextSibling !== null &&
    isNodeDisplayInlineByDefault(currentEle.nextSibling.nodeName) &&
    currentEle.nextSibling.nodeValue &&
    currentEle.nextSibling.nodeValue.trim().length > 0;

  return (
    (prevSiblingIsText && nextSiblingIsText) ||
    (!ele.previousSibling && nextSiblingIsText) ||
    (prevSiblingIsText && !ele.nextSibling)
  );
}

/* Determine if a node's default "display" property is "inline". This list is taken from https://www.w3schools.com/html/html_blocks.asp */
const inlineElements = new Set([
  'A',
  'ABBR',
  'ACRONYM',
  'B',
  'BDO',
  'BIG',
  'BR',
  'BUTTON',
  'CITE',
  'CODE',
  'DFN',
  'EM',
  'I',
  'IMG',
  'INPUT',
  'KBD',
  'LABEL',
  'MAP',
  'OBJECT',
  'Q',
  'SAMP',
  'SCRIPT',
  'SELECT',
  'SMALL',
  'SPAN',
  'STRONG',
  'SUB',
  'SUP',
  'TEXTAREA',
  'TIME',
  'TT',
  'VAR',
  '#TEXT',
]);

export function isNodeDisplayInlineByDefault(nodeName: string) {
  return inlineElements.has(nodeName.toUpperCase());
}
